import React, { useState } from 'react';

const SECTIONS = [
  { id: 'collect', title: 'Information We Collect' },
  { id: 'entered-data', title: 'Data You Enter About Others' },
  { id: 'use', title: 'How We Use Your Information' },
  { id: 'google', title: 'Google Account & Gmail Access' },
  { id: 'biometric', title: 'Biometric Data' },
  { id: 'whatsapp', title: 'WhatsApp Data' },
  { id: 'sharing', title: 'Data Sharing' },
  { id: 'retention', title: 'Data Retention' },
  { id: 'payments', title: 'Payment & Financial Data' },
  { id: 'security', title: 'Data Security' },
  { id: 'breach', title: 'Data Breach Notification' },
  { id: 'rights', title: 'Your Rights' },
  { id: 'children', title: "Children's Privacy" },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'grievance', title: 'Grievance Officer' },
  { id: 'contact', title: 'Contact Us' },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState('collect');

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', color: '#111827' }}>
      <style>{`
        @media (max-width: 768px) {
          .privacy-body-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 32px 20px 64px !important;
          }
          .privacy-toc {
            position: static !important;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 16px;
            margin-bottom: 8px;
          }
          .privacy-toc > div:last-child {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 6px !important;
            border-left: none !important;
          }
          .privacy-toc button {
            padding: 5px 10px !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 999px !important;
            margin-left: 0 !important;
          }
        }
      `}</style>

      {/* ── NAV ── */}
      <header style={{
        background: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '0 16px',
        height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img src="/logo-icon.png" alt="CloudAIRambo logo" style={{ width: 34, height: 34, objectFit: 'contain' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontWeight: 700, fontSize: '18px', color: '#111827', letterSpacing: '-0.01em' }}>CloudAIRambo</span>
        </a>
        <a href="/" style={{ fontSize: '14px', color: '#6b7280', textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
          onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
        >← Back to home</a>
      </header>

      {/* ── HERO ── */}
      <div style={{ textAlign: 'center', padding: '64px 24px 48px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
        <img src="/logo.png" alt="CloudAIRambo" style={{ height: 80, width: 'auto', objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: '#f0f4ff', border: '1px solid #c7d4fe', borderRadius: '999px',
          padding: '5px 14px', fontSize: '12px', color: '#4f46e5', fontWeight: 600, marginBottom: '24px',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />
          Legal
        </div>
        <h1 style={{ fontSize: 'clamp(28px, 8vw, 44px)', fontWeight: 800, color: '#111827', margin: '0 0 12px', letterSpacing: '-0.02em' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>Last updated: June 26, 2026</p>
      </div>

      {/* ── BODY ── */}
      <div className="privacy-body-grid" style={{ maxWidth: '1100px', margin: '0 auto', padding: '56px 32px 96px', display: 'grid', gridTemplateColumns: '240px 1fr', gap: '64px' }}>

        {/* ── TOC (sticky sidebar) ── */}
        <nav className="privacy-toc" style={{ position: 'sticky', top: '84px', alignSelf: 'start' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>
            On this page
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', borderLeft: '2px solid #e5e7eb' }}>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => { scrollTo(s.id); setActiveSection(s.id); }}
                style={{
                  textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '7px 0 7px 16px', fontSize: '13px', fontWeight: 500,
                  color: activeSection === s.id ? '#4f46e5' : '#6b7280',
                  borderLeft: activeSection === s.id ? '2px solid #4f46e5' : '2px solid transparent',
                  marginLeft: '-2px', transition: 'color 0.15s',
                }}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* ── CONTENT ── */}
        <div style={{ fontSize: '15px', lineHeight: 1.75, color: '#374151', maxWidth: '640px' }}>

          <p>
            CloudAIRambo ("we", "our", or "us") operates the CloudAIRambo platform available at{' '}
            <a href="https://cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>cloudairambo.com</a>.
            This Privacy Policy explains what information we collect, how we use it, and the choices you have.
          </p>

          <Section id="collect" title="1. Information We Collect">
            <ul>
              <Li><strong>Account information</strong> — name, email address, and profile picture, when you register directly or sign in with Google.</Li>
              <Li><strong>Usage data</strong> — pages visited, features used, and actions taken within the app, for reliability and troubleshooting.</Li>
              <Li><strong>Business data</strong> — invoices, clients, inventory, employee records, and other data you enter into the platform.</Li>
              <Li><strong>Biometric data (optional)</strong> — if you or your organization enables face recognition or fingerprint-based attendance features (e.g. for gym or canteen member check-in), we collect and store a facial descriptor and/or fingerprint credential for each enrolled individual.</Li>
            </ul>
          </Section>

          <Section id="entered-data" title="1A. Data You Enter About Others">
            <p>
              As a business using CloudAIRambo, you may enter personal data about your own clients, customers,
              employees, or members into the platform — for example, names, phone numbers, emails, or payment
              amounts on an invoice, or attendance records for staff or gym/canteen members.
            </p>
            <ul>
              <Li>You are the data controller for this information, and you are responsible for having a lawful basis (such as consent) to collect and enter it, and for your own privacy obligations to those individuals.</Li>
              <Li>We act as a data processor for this information — we store and process it solely to provide the platform to you, and do not use it for our own purposes such as marketing, profiling, or advertising.</Li>
              <Li>We do not contact your clients, customers, employees, or members directly, except to deliver messages you have configured the platform to send on your behalf (e.g. an invoice, a receipt, or an attendance confirmation).</Li>
            </ul>
          </Section>

          <Section id="use" title="2. How We Use Your Information">
            <ul>
              <Li>To provide and operate the CloudAIRambo platform.</Li>
              <Li>To authenticate your identity and manage your account.</Li>
              <Li>To send invoices, receipts, and reminders you generate, to your clients.</Li>
              <Li>To send important service updates and notifications.</Li>
              <Li>To improve our product based on usage patterns.</Li>
            </ul>
          </Section>

          <Section id="google" title="3. Google Account & Gmail Access">
            <p>
              When you sign in with Google, we receive your name, email address, and profile picture from Google.
              We do not access your Google Drive or any other Google service beyond what's described here.
            </p>
            <p>
              If you separately choose to <strong>connect Gmail for sending</strong> (Profile → Gmail for Sending
              Emails), we request Google's <code style={codeStyle}>gmail.send</code> permission. This is used{' '}
              <strong>only</strong> to send invoices, receipts, and payment reminders on your behalf, triggered by
              actions you take inside CloudAIRambo.
            </p>
            <ul>
              <Li>We do not read, store, or analyze the contents of your Gmail inbox.</Li>
              <Li>We do not use Gmail data for advertising.</Li>
              <Li>We store only the access/refresh tokens needed to send mail on your behalf, and the connected address.</Li>
              <Li>You can disconnect this at any time from your Profile page — this immediately deletes the stored tokens.</Li>
            </ul>
            <p>
              Our use and transfer of information received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener" style={{ color: '#4f46e5', fontWeight: 500 }}>
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
          </Section>

          <Section id="biometric" title="3A. Biometric Data">
            <p>
              If your organization enables optional attendance features such as face recognition or fingerprint
              scanning, we collect and store biometric identifiers — a facial descriptor and/or fingerprint
              credential — for the individuals enrolled (e.g. employees, gym members, or canteen members). This
              data is used solely to verify attendance or identity within the platform.
            </p>
            <ul>
              <Li>We do not sell, rent, share, or disclose biometric data to any third party, advertiser, or other organization for any purpose, including marketing or advertising.</Li>
              <Li>Biometric data is stored in our secured database with the same access controls and encryption-in-transit protections as the rest of your account data.</Li>
              <Li>Biometric data is never used for any purpose beyond the attendance/verification feature it was collected for.</Li>
              <Li>The organization enabling this feature is responsible for obtaining any consent required from the individual being enrolled, in accordance with applicable law, including India's Digital Personal Data Protection Act.</Li>
              <Li>Biometric data can be deleted at any time by removing the associated member or employee profile, or by contacting us below.</Li>
            </ul>
          </Section>

          <Section id="whatsapp" title="3B. WhatsApp Data">
            <p>
              CloudAIRambo offers WhatsApp-based messaging (e.g. sending invoices, receipts, and attendance
              notifications) through two possible connection methods:
            </p>
            <ul>
              <Li><strong>QR / Linked Device connection</strong> — a convenience method that links your WhatsApp number to the platform by scanning a QR code, without requiring a formal WhatsApp Business API account. Messages sent this way pass through our messaging infrastructure to reach WhatsApp's servers.</Li>
              <Li><strong>WhatsApp Business API</strong> — an official, Meta-verified integration you can optionally connect instead, using your own Business API credentials.</Li>
            </ul>
            <p>
              In either case, we store the phone numbers you message (typically your clients, employees, or
              members) and the content of automated messages sent through the platform (e.g. invoice links,
              attendance confirmations) solely to deliver that message. We do not read or store your personal
              WhatsApp conversations, contacts, or any messages sent outside the platform's automated flows. We
              do not sell or share WhatsApp data with third parties for marketing purposes.
            </p>
            <p>
              <strong>Sender responsibility.</strong> As the account holder connecting your WhatsApp number, you
              are solely responsible for the content of the messages you send, for having the recipient's
              consent or an existing business relationship as required by applicable law, and for compliance
              with WhatsApp's own terms of service and messaging policies. We are not responsible for the
              content, delivery, or consequences of messages sent through your connected WhatsApp number.
            </p>
          </Section>

          <Section id="sharing" title="4. Data Sharing">
            <p>
              We do not sell, trade, or rent your personal information to third parties. We share data only with
              service providers who help us operate the platform (our "sub-processors"), and only to the extent
              necessary for them to perform their function. These currently include:
            </p>
            <ul>
              <Li><strong>Cloud hosting & infrastructure</strong> — to store and run the platform and its database.</Li>
              <Li><strong>Payment gateway providers</strong> — to process subscription payments and customer transactions. We do not store full card or payment credentials ourselves; these are handled directly by the gateway.</Li>
              <Li><strong>WhatsApp / messaging infrastructure</strong> — to deliver invoices, receipts, and notifications you send through the platform.</Li>
              <Li><strong>SMS / OTP delivery providers</strong> — to send one-time passwords and verification codes.</Li>
              <Li><strong>Email delivery providers</strong> (including Gmail, if you connect it) — to send invoices and notifications on your behalf.</Li>
              <Li><strong>Text-to-speech and translation services</strong> — to generate audio and translated content for features that use them.</Li>
            </ul>
            <p>
              Each of these sub-processors is contractually restricted from using your data for any purpose
              other than providing their service to us. We may also disclose information when required by law,
              to comply with a valid legal request, or to protect the rights, property, or safety of
              CloudAIRambo, our users, or the public.
            </p>
          </Section>

          <Section id="retention" title="5. Data Retention">
            <p>
              We retain your account and business data for as long as your account is active. If you disconnect
              Gmail, we delete the associated tokens immediately. If you delete your account, we delete or
              anonymize your personal data, except where retention is required by law.
            </p>
          </Section>

          <Section id="payments" title="5A. Payment & Financial Data">
            <p>
              <strong>Your own subscription payments</strong> to CloudAIRambo are processed by our payment
              gateway partner. We do not receive or store your full card number, CVV, or other sensitive payment
              credentials — these are handled entirely within the gateway's own secure, compliant systems.
            </p>
            <p>
              <strong>Payments your customers make to you</strong> (e.g. when a client pays an invoice you sent
              through the platform) are a transaction between your customer and your own connected payment
              gateway account. We help facilitate this connection via API and webhook integration with your
              chosen gateway, so that payment status updates (e.g. "paid," "failed") sync automatically to your
              invoice. However, we are not a party to the transaction itself, do not process or hold those
              funds, and do not receive your customers' payment credentials (card numbers, CVV, UPI PIN, etc.) —
              these remain entirely within the gateway's own secure systems. Through the webhook connection, we
              receive and store only transaction metadata reported by the gateway, such as payment status,
              amount, and a transaction reference, for your own business records and legal compliance.
            </p>
            <p>
              These transactional records are retained in line with the financial record-keeping requirements
              described in our Terms of Service.
            </p>
          </Section>

          <Section id="security" title="6. Data Security">
            <p>
              We use industry-standard security measures, including encrypted connections (HTTPS) and secure
              token-based authentication, to protect your data.
            </p>
          </Section>

          <Section id="breach" title="6A. Data Breach Notification">
            <p>
              In the event of a security incident that results in unauthorized access to, or disclosure of,
              your personal data or your customers' data stored on the Service, we will take reasonable steps
              to investigate and contain the incident, and will notify you without undue delay once we have
              enough information to do so, consistent with our obligations under applicable law. Notification
              will be sent to the email address associated with your account and may include the nature of the
              incident, the categories of data affected, and the steps we are taking in response. We are not
              responsible for breaches occurring at third-party services you connect to the platform (e.g. your
              payment gateway, WhatsApp/Meta, or SMS provider), though we will assist in coordinating a response
              where reasonably possible.
            </p>
          </Section>

          <Section id="rights" title="7. Your Rights">
            <p>
              Depending on where you're located, you may have certain rights over your personal data. We aim to
              honor these rights for all users, regardless of location:
            </p>
            <ul>
              <Li><strong>Access</strong> — request a copy of the personal data we hold about you.</Li>
              <Li><strong>Correction</strong> — request that we correct inaccurate or incomplete data.</Li>
              <Li><strong>Erasure</strong> — request deletion of your account and associated personal data, subject to financial/legal record-keeping requirements described in our Terms of Service.</Li>
              <Li><strong>Objection / Opt-out</strong> — object to certain uses of your data, such as marketing communications.</Li>
              <Li><strong>Withdraw consent</strong> — where we rely on your consent (e.g. biometric attendance features), withdraw it at any time going forward.</Li>
              <Li>Disconnect Gmail at any time from Profile → Gmail for Sending Emails, or revoke access directly from your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener" style={{ color: '#4f46e5', fontWeight: 500 }}>Google Account permissions page</a>.</Li>
            </ul>
            <p>
              To exercise any of these rights, contact us using the details below. We may need to verify your
              identity before processing certain requests.
            </p>
          </Section>

          <Section id="children" title="8. Children's Privacy">
            <p>
              CloudAIRambo is intended for business use by adults and is not directed at children under 13. We do
              not knowingly collect data from children.
            </p>
          </Section>

          <Section id="changes" title="9. Changes to This Policy">
            <p>
              We may update this policy from time to time. We'll update the "Last updated" date above whenever we
              make changes. Continued use of CloudAIRambo after changes means you accept the updated policy.
            </p>
          </Section>

          <Section id="grievance" title="10. Grievance & Data Protection Contact">
            <p>
              If you have any concerns, complaints, or requests regarding your personal data, you can reach our
              designated data protection contact below. Depending on your country of residence, you may also
              have the right to lodge a complaint with your local data protection authority.
            </p>
            <div style={{
              background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '14px',
              padding: '24px 28px', marginTop: '12px',
            }}>
              <p style={{ margin: '0 0 6px' }}><strong>Data Protection Contact</strong></p>
              <p style={{ margin: 0 }}>
                Email:{' '}
                <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 600 }}>
                  support@cloudairambo.com
                </a>
              </p>
            </div>
            <p style={{ marginTop: '14px' }}>
              We will acknowledge and respond to all data-related requests and grievances within a reasonable
              timeframe.
            </p>
          </Section>

          <Section id="contact" title="11. Contact Us">
            <div style={{
              background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '14px',
              padding: '24px 28px', marginTop: '12px',
            }}>
              <p style={{ margin: 0 }}>
                Questions about this policy or your data? Reach us at{' '}
                <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 600 }}>
                  support@cloudairambo.com
                </a>
              </p>
            </div>
          </Section>

        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
        <div style={{
          maxWidth: '1100px', margin: '0 auto', padding: '24px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: '13px', color: '#374151' }}>© 2026 CloudAIRambo. All rights reserved.</span>
          <a href="/terms" style={{ fontSize: '13px', color: '#4b5563', textDecoration: 'none', fontWeight: 500 }}>
            Terms of Service
          </a>
        </div>
      </footer>
    </div>
  );
}

// ─── Small helpers ──────────────────────────────────────────────────────────

const codeStyle: React.CSSProperties = {
  background: '#f1f5f9', padding: '2px 7px', borderRadius: '5px', fontSize: '13px', fontFamily: 'monospace',
};

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginTop: '48px', scrollMarginTop: '84px' }}>
      <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#111827', marginBottom: '14px' }}>{title}</h2>
      {children}
    </section>
  );
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li style={{ marginBottom: '8px', paddingLeft: '4px' }}>{children}</li>
  );
}
